import { isTimelinePart, TRACE_TOOL_TYPES } from "@/lib/chat-render-items";
import {
  convertUIMessagePartsToText,
  isCompactionMessage,
  stripUnrecognizedShaarpTags,
} from "@/lib/ai";
import type {
  AskClarificationPartType,
  ChatForcedTool,
  ChatRequestMode,
  ChatUIMessage,
} from "@shaarp/common";
import type { ToolUIPart } from "ai";

/** Tool part a clarifying question travels in. */
export const CLARIFICATION_PART_TYPE: AskClarificationPartType =
  "tool-askClarification";

/** One question of a run, and the reply it got when it got one. */
export type ClarificationStep = {
  /** The message the question was asked in, and its rank within it. */
  readonly id: string;
  readonly part: ToolUIPart;
  readonly answer?: string;
  /**
   * The reply walked past the question rather than answering it, so it says
   * nothing about the search and is not counted as a precision.
   */
  readonly isSkipped?: boolean;
};

/**
 * A thread entry as it renders: either a message on its own, or a run of
 * clarifying questions and their replies merged into a single card.
 */
export type ThreadRenderItem =
  | {
      readonly kind: "message";
      readonly message: ChatUIMessage;
      /** Position in the thread, which the message reads its turn state from. */
      readonly index: number;
      /**
       * The reply the question this message asks got, when it asks one beside
       * other content — a lead-in sentence, typically — and so renders in
       * place instead of joining a run.
       */
      readonly questionReply?: ClarificationReply;
      /** Messages this item renders, beyond its own: the reply it swallowed. */
      readonly absorbedMessageIds?: readonly string[];
    }
  | {
      readonly kind: "clarificationRun";
      readonly key: string;
      /** Every message the run swallowed, in thread order. */
      readonly messageIds: readonly string[];
      /** Whether the run ends the thread, so its last question is still open. */
      readonly isTail: boolean;
      readonly steps: readonly ClarificationStep[];
    };

/**
 * The questions a message asks, when asking them is all the message does.
 *
 * Normally one: the model is told to ask a single question per call and the
 * turn ends on the call. Nothing but that instruction stops it emitting two in
 * the same step though, so they are all collected — two questions dropped into
 * two detached cards, with only the first one persisted, is a worse failure
 * than two steps in one card.
 *
 * A question written under an answer, or called alongside another tool, is not
 * a step of a run: merging it would drop everything else the message carries.
 */
function getQuestionParts(message: ChatUIMessage): readonly ToolUIPart[] {
  if (message.role !== "assistant" || message.metadata?.error) return [];

  // Looked up before anything else: this runs over the whole thread on every
  // stream delta, and the parts of a message that asked nothing must not be
  // read any further than their type.
  const questions = message.parts.filter(
    (part) => part.type === CLARIFICATION_PART_TYPE
  );
  if (questions.length === 0) return [];

  const carriesMore = message.parts.some((part) => {
    if (part.type === CLARIFICATION_PART_TYPE) return false;
    // Skipped for the same reason the message itself skips them: the stream
    // emits empty text parts and one `step-start` per model step, and reasoning
    // is not rendered in the thread.
    if (part.type === "step-start" || part.type === "reasoning") return false;
    if (isTimelinePart(part)) return false;
    if (part.type === "text") return part.text.trim().length > 0;
    return true;
  });

  return carriesMore ? [] : (questions as ToolUIPart[]);
}

/**
 * Whether a message asks exactly one clarifying question, whatever else it
 * carries — which is what makes its reply attributable without ambiguity.
 */
function asksOneQuestion(message: ChatUIMessage): boolean {
  if (message.role !== "assistant" || message.metadata?.error) return false;

  return (
    message.parts.filter((part) => part.type === CLARIFICATION_PART_TYPE)
      .length === 1
  );
}

/**
 * Whether this message is the assistant putting a question to the user.
 *
 * Read by the thread to decide what a reply to it should carry over — a tool
 * the user forced, in particular: the turn that asks the question is not the
 * turn that searches, and the force would otherwise be spent on the question.
 *
 * @param message A message of the thread, or none.
 * @returns Whether it asks at least one clarifying question.
 */
export function asksClarification(message: ChatUIMessage | undefined): boolean {
  if (!message || message.role !== "assistant" || message.metadata?.error) {
    return false;
  }

  return message.parts.some((part) => part.type === CLARIFICATION_PART_TYPE);
}

/** The forced tool or request mode a message is sent with — one at most. */
export type ChatRequestAim = {
  readonly forcedTool?: ChatForcedTool | null;
  readonly requestMode?: ChatRequestMode | null;
};

/** The last thing the user said, whatever the assistant has said since. */
function findLastUserMessage(
  messages: readonly ChatUIMessage[],
): ChatUIMessage | undefined {
  for (let index = messages.length - 1; index >= 0; index--) {
    if (messages[index].role === "user") return messages[index];
  }

  return undefined;
}

/**
 * What a message is aimed with once the clarification run it may reply to has
 * had its say.
 *
 * A forced tool or a mode is picked for a request, and the questioning that
 * follows is that same request still being aimed: the composer clears either
 * as soon as the first message goes, so without this "search the web" is
 * spent on the turn that asked a question rather than on the one that
 * searches. The aim rides every reply for as long as the run keeps asking,
 * and stops the moment the assistant answers instead — which is the turn it
 * was picked for.
 *
 * Whatever the user picks on the reply itself replaces the inherited aim
 * whole: the two are exclusive, and the API keeps the tool when both arrive,
 * so an inherited tool would silently cost the user the mode they just chose.
 *
 * @param thread The thread before the message is sent.
 * @param aim What the composer sends the message with.
 */
export function resolveRequestAim(
  thread: readonly ChatUIMessage[],
  aim: ChatRequestAim,
): ChatRequestAim {
  if (aim.forcedTool || aim.requestMode) return aim;
  if (!asksClarification(thread[thread.length - 1])) return {};

  const metadata = findLastUserMessage(thread)?.metadata;
  if (metadata?.forcedTool) return { forcedTool: metadata.forcedTool };
  return { requestMode: metadata?.requestMode };
}

/** What a message replied to the question before it. */
export type ClarificationReply = {
  readonly text: string;
  /** The reply walked past the question instead of answering it. */
  readonly isSkipped: boolean;
};

/**
 * The reply resolved for a message, kept so the same message always hands back
 * the same object.
 *
 * This runs over the whole thread on every stream delta, and its result is a
 * prop of `ChatMessage` — a `memo` whose whole job is to leave the settled
 * messages alone while an answer streams. A fresh object per pass fails its
 * comparison for every message that carries a reply, re-rendering each of those
 * subtrees, markdown and all, on every token.
 *
 * Keyed on the message itself: the SDK rebuilds the thread array on each delta
 * but keeps the objects of the turns it is not writing, so an entry lives
 * exactly as long as the message it was read from.
 */
const replyCache = new WeakMap<ChatUIMessage, ClarificationReply | null>();

/**
 * The reply a message gives to the question before it, or `null` when it is not
 * one.
 *
 * A bubble carrying files, a prompt or a company context renders pills the
 * compact row of a step cannot show, so it keeps its bubble and ends the run
 * rather than losing them.
 *
 * A forced tool is not one of those. It is carried over from the turn that
 * asked the question — the search it forces is the one that comes *after* the
 * answer — so treating it as content would break the card for the very user
 * who ticked "search the web" before typing their reply.
 */
function readReply(
  message: ChatUIMessage | undefined
): ClarificationReply | null {
  if (!message || message.role !== "user") return null;

  const cached = replyCache.get(message);
  if (cached !== undefined) return cached;

  const reply = computeReply(message);
  replyCache.set(message, reply);
  return reply;
}

/** {@link readReply} without the cache — the read itself. */
function computeReply(message: ChatUIMessage): ClarificationReply | null {
  // A compaction boundary is persisted as a user row carrying the summary as
  // its text, so without this a run whose next turn crossed one adopted the
  // summary as the precision the user gave. The run branch steps over these
  // already; the in-place branch reads this function directly, which is why
  // the guard belongs here rather than beside either caller.
  if (isCompactionMessage(message)) return null;

  const metadata = message.metadata;
  if (metadata?.prompt || metadata?.companyContext) return null;
  if (metadata?.files?.length) return null;
  if (message.parts.some((part) => part.type === "file")) return null;

  const text = stripUnrecognizedShaarpTags(
    convertUIMessagePartsToText(message)
  );
  if (text.trim().length === 0) return null;

  return { text, isSkipped: !!metadata?.clarificationSkipped };
}

/**
 * Turns the thread into what it renders, merging each run of clarifying
 * questions into one card.
 *
 * The assistant asks one question per turn on purpose — the next one is picked
 * from the answer to the last — so a single exchange arrives as an alternation
 * of question turns and reply messages. Grouped here rather than inside a
 * message, which only ever sees its own parts.
 *
 * @param messages The thread, oldest first.
 * @returns One item per rendered entry, questions and replies merged.
 */
export function buildThreadRenderItems(
  messages: ChatUIMessage[]
): ThreadRenderItem[] {
  const items: ThreadRenderItem[] = [];
  let cursor = 0;

  // Ids of messages that render nothing and are waiting for somewhere to hang
  // their DOM anchor — a compaction boundary, so far. Handed to the next item
  // rather than wrapped in one of their own: an empty child of the thread's
  // stack still takes a full gap, which is what put 56px between the two
  // messages a compaction sits between.
  let pendingAnchorIds: string[] = [];
  const takeAnchorIds = (): string[] => {
    const ids = pendingAnchorIds;
    pendingAnchorIds = [];
    return ids;
  };

  while (cursor < messages.length) {
    let questionParts = getQuestionParts(messages[cursor]);
    if (questionParts.length === 0) {
      const index = cursor;
      const message = messages[index];
      cursor += 1;

      if (isCompactionMessage(message)) {
        pendingAnchorIds.push(message.id);
        continue;
      }

      // A question the assistant introduced with a sentence of its own renders
      // in place instead of joining a run, and the card it renders in only ever
      // sees the parts of its own message. So the reply is resolved here too,
      // or a question that was answered right below reported itself unanswered.
      const reply = asksOneQuestion(message)
        ? readReply(messages[cursor])
        : null;
      if (!reply) {
        items.push({
          kind: "message",
          message,
          index,
          absorbedMessageIds: takeAnchorIds(),
        });
        continue;
      }

      items.push({
        kind: "message",
        message,
        index,
        questionReply: reply,
        absorbedMessageIds: [...takeAnchorIds(), messages[cursor].id],
      });
      cursor += 1;
      continue;
    }

    const startIndex = cursor;
    const messageIds: string[] = [];
    const steps: ClarificationStep[] = [];

    // A compaction boundary renders nothing, so a run steps over it rather than
    // ending on it — its id is kept all the same, or a search jump to it would
    // have nowhere to land.
    const skipTransparentMessages = (): void => {
      while (cursor < messages.length && isCompactionMessage(messages[cursor])) {
        messageIds.push(messages[cursor].id);
        cursor += 1;
      }
    };

    while (questionParts.length > 0) {
      const questionMessage = messages[cursor];
      messageIds.push(questionMessage.id);
      cursor += 1;
      skipTransparentMessages();

      const reply = readReply(messages[cursor]);
      // A reply answers the question it follows. When a turn asked several at
      // once there is still only one reply, so it lands on the last of them and
      // the others read as left behind — which is what happened.
      questionParts.forEach((part, questionIndex) => {
        const isLastQuestion = questionIndex === questionParts.length - 1;
        const id = `${questionMessage.id}#${questionIndex}`;
        steps.push(
          reply && isLastQuestion
            ? { id, part, answer: reply.text, isSkipped: reply.isSkipped }
            : { id, part }
        );
      });
      if (!reply) break;

      messageIds.push(messages[cursor].id);
      cursor += 1;
      skipTransparentMessages();
      questionParts =
        cursor < messages.length ? getQuestionParts(messages[cursor]) : [];
    }

    items.push({
      kind: "clarificationRun",
      // Keyed on the first question so the card keeps its identity — and the
      // free-text draft being typed in it — as the run grows a step.
      key: `clarification-${messages[startIndex].id}`,
      // The run's own first message stays first: it is the anchor the wrapper
      // itself carries, and the one a highlight lands on.
      messageIds: [...messageIds, ...takeAnchorIds()],
      isTail: cursor >= messages.length,
      steps,
    });
  }

  // A thread ending on a compaction boundary: the anchors go on the last item
  // rendered, and on nothing at all when there is none to put them on.
  const lastItem = items[items.length - 1];
  if (pendingAnchorIds.length > 0 && lastItem) {
    items[items.length - 1] =
      lastItem.kind === "clarificationRun"
        ? {
            ...lastItem,
            messageIds: [...lastItem.messageIds, ...takeAnchorIds()],
          }
        : {
            ...lastItem,
            absorbedMessageIds: [
              ...(lastItem.absorbedMessageIds ?? []),
              ...takeAnchorIds(),
            ],
          };
  }

  return items;
}

/**
 * Whether the user message at `userIndex` replies to a clarifying question:
 * the turn it starts takes those answers into account, and says so. A
 * compaction boundary between the two is skipped — it is no message of the
 * conversation.
 */
export function followsClarificationReply(
  messages: readonly ChatUIMessage[],
  userIndex: number
): boolean {
  // A compaction boundary can sit between the reply and the answer to it.
  let replyIndex = userIndex;
  while (
    replyIndex > 0 &&
    messages[replyIndex] &&
    isCompactionMessage(messages[replyIndex])
  ) {
    replyIndex -= 1;
  }
  if (messages[replyIndex]?.role !== "user") return false;
  for (let index = replyIndex - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (isCompactionMessage(message)) continue;
    return (
      message.role === "assistant" &&
      message.parts.some((part) => part.type === CLARIFICATION_PART_TYPE)
    );
  }
  return false;
}

/** The message after `index` that is not a compaction boundary, if any. */
function nextConversationIndex(
  messages: readonly ChatUIMessage[],
  index: number
): number | null {
  for (let next = index + 1; next < messages.length; next += 1) {
    if (!isCompactionMessage(messages[next])) return next;
  }
  return null;
}

/**
 * The answer that carries on the work of the message at `index`, when that
 * message called a tool of the card of work — a search, the CRM, the company
 * data — then asked a question the user replied to. The thread shows the two turns' work as one card, at
 * the top, rather than two cards cut apart by the question.
 *
 * @param isAnswerAwaited The reply has just been sent and its answer has not
 * started: the card holds its place.
 * @returns The answer's position, null while it is awaited; or null when the
 * message's work is not carried on — no work, no reply, or an answer that
 * asked again or failed, which keeps its own card.
 */
export function findWorkContinuation(
  messages: readonly ChatUIMessage[],
  index: number,
  isAnswerAwaited: boolean
): { readonly index: number | null } | null {
  const message = messages[index];
  if (!asksClarification(message)) return null;
  // A message that only reasoned before asking joins a run of questions, and
  // has no card of its own to carry on.
  if (!message.parts.some((part) => TRACE_TOOL_TYPES.has(part.type))) {
    return null;
  }

  const replyIndex = nextConversationIndex(messages, index);
  if (replyIndex === null || messages[replyIndex].role !== "user") return null;

  const answerIndex = nextConversationIndex(messages, replyIndex);
  if (answerIndex === null) return isAnswerAwaited ? { index: null } : null;
  const answer = messages[answerIndex];
  if (
    answer.role !== "assistant" ||
    answer.metadata?.error ||
    asksClarification(answer)
  ) {
    return null;
  }
  return { index: answerIndex };
}
